import React, { ReactNode } from 'react';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { ImageInterface } from '../../types';
import Spacer from '../../helpers/Spacer';
import { Grid, GridItem } from '../../helpers';
import { animations, colors, mixins } from '../../core/styles/theme/default';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import { Link } from '../../primitives';
import i18n from './intro-i18n';

const StyledIntro = styled(Grid)(
  ({ theme }: { theme: Theme }) => css`
    .gatsby-image-wrapper {
      ${mixins.responsiveStyles({
        'aspect-ratio': {
          zero: '450 / 280',
          small: '735 / 460',
          medium: '975 / 540',
          large: '1190 / 450',
          wide: '1090 / 420',
          ultra: '1260 / 480',
        },
      })}
    }

    color: ${colors.grey700};

    ${theme?.stylesExtension?.intro?.default}
  `,
);

const StyledSocialLink = styled(Link)`
  font-size: ${rem(14)};
  line-height: ${rem(20)};
  font-weight: 500;
  text-decoration: none;
  color: ${colors.black};

  svg {
    margin-left: ${rem(6)};

    path {
      fill: ${colors.blue900};
      transition: fill ${animations.fast};
      transform: scale(2.75);
      transform-origin: center;
    }
  }
`;

const Intro = ({
  leadText,
  leadImage,
  shareUrl,
  fullWidth = false,
  locale = 'de',
}: IntroProps) => {
  if (!leadText && !leadImage && !shareUrl) return null;

  return (
    <StyledIntro columns={12}>
      {leadText && (
        <GridItem
          size={fullWidth ? 12 : { zero: 12, medium: 10, large: 8 }}
          push={fullWidth ? 0 : { zero: 0, medium: 1, large: 2 }}
        >
          <Spacer space="gamma" />
          {leadText}
          <Spacer space="mega" />
        </GridItem>
      )}
      {shareUrl ? (
        <GridItem
          size={fullWidth ? 12 : { zero: 12, medium: 10, large: 8 }}
          push={fullWidth ? 0 : { zero: 0, medium: 1, large: 2 }}
        >
          {!leadText && <Spacer space="gamma" />}
          <StyledSocialLink
            linkText={i18n.shareOn[locale]}
            link={{
              href: `https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`,
              title: `${i18n.shareOn[locale]} LinkedIn`,
              target: '_blank',
            }}
            iconAfter="EchIconSocialMediaLinkedin2White"
            iconSize="25"
          />
          <Spacer space="mega" />
        </GridItem>
      ) : null}
      {leadImage?.image && (
        <GridItem size={12} push={0}>
          {!leadText && !shareUrl && <Spacer space="gamma" />}
          <ImageWithCaption
            data={{
              ...leadImage,
              imageSizes: {
                0: [450, 280],
                480: [735, 460],
                768: [975, 540],
                1024: [1190, 450],
                1280: [1090, 420],
                1440: [1260, 480],
              },
              loading: 'eager',
            }}
          />
          <Spacer space="mega" />
        </GridItem>
      )}
    </StyledIntro>
  );
};

export type IntroProps = {
  leadText: ReactNode;
  leadImage?: ImageInterface;
  shareUrl?: string;
  fullWidth?: boolean;
  locale?: string;
};

export default Intro;
